import { canEnhancerUploadFileReachSupportedMimeType } from '@/ai/image/upload-image-preprocess-policy';
import type { WorkspacePrefillRequestV1 } from '@/cms/components/blocks/workspace/workspace-prefill-contract';
import type {
  WorkspaceCreditCalculationTool,
  WorkspaceToolParamView,
  WorkspaceToolPrefillFieldView,
  WorkspaceToolView,
} from '../contracts/tool-view';

export type WorkspacePrefillFormModel = {
  version: 1;
  toolId: string;
  toolLabel: string;
  uploadLimits: WorkspacePrefillUploadLimits;
  creditQuoteTool: WorkspaceCreditCalculationTool;
  fields: WorkspaceToolPrefillFieldView[];
  parameterFields: WorkspaceToolParamView[];
};

export type WorkspacePrefillUploadLimits = {
  freeMaxSingleBytes: number;
  paidMaxSingleBytes: number;
};

export type WorkspacePrefillValidationIssue = {
  code:
    | 'invalid-request'
    | 'too-many-files'
    | 'unsupported-file-type'
    | 'file-too-large';
  fieldId: string;
  message: string;
};

export type WorkspacePrefillValidationResult =
  | {
      ok: true;
      value: WorkspacePrefillRequestV1;
    }
  | {
      ok: false;
      issues: WorkspacePrefillValidationIssue[];
    };

type WorkspacePrefillToolProjection = Pick<
  WorkspaceToolView,
  | 'toolId'
  | 'label'
  | 'creditPolicy'
  | 'fields'
  | 'inputBindings'
  | 'prefillFields'
  | 'viewKind'
> & {
  runtimeCaps: Pick<
    WorkspaceToolView['runtimeCaps'],
    'creditCalculationMode' | 'freeMaxSingleBytes' | 'paidMaxSingleBytes'
  >;
};

function requireSingleRoleField(params: {
  fields: WorkspaceToolPrefillFieldView[];
  role: 'primaryImage' | 'prompt';
  bindingId: string | null | undefined;
  toolId: string;
}) {
  const roleFields = params.fields.filter(
    (field) => field.inputRole === params.role
  );
  if (roleFields.length > 1) {
    throw new Error(
      `Workspace prefill Tool ${params.toolId} must define at most one ${params.role} field.`
    );
  }

  const roleField = roleFields[0];
  if (roleField && roleField.id !== params.bindingId) {
    throw new Error(
      `Workspace prefill Tool ${params.toolId} ${params.role} binding does not match field ${roleField.id}.`
    );
  }
}

export function buildWorkspacePrefillFormModel(
  tool: WorkspacePrefillToolProjection
): WorkspacePrefillFormModel {
  if (!tool.prefillFields) {
    throw new Error(
      `Workspace prefill Tool ${tool.toolId} is missing its prefill field projection.`
    );
  }
  const fieldIds = new Set<string>();
  for (const field of tool.prefillFields) {
    if (fieldIds.has(field.id)) {
      throw new Error(
        `Workspace prefill Tool ${tool.toolId} contains duplicate field ${field.id}.`
      );
    }
    fieldIds.add(field.id);
  }

  requireSingleRoleField({
    fields: tool.prefillFields,
    role: 'primaryImage',
    bindingId: tool.inputBindings?.primaryImageFieldId,
    toolId: tool.toolId,
  });
  requireSingleRoleField({
    fields: tool.prefillFields,
    role: 'prompt',
    bindingId: tool.inputBindings?.promptFieldId,
    toolId: tool.toolId,
  });

  const referenceBindingIds = new Set(
    tool.inputBindings?.referenceImageFieldIds ?? []
  );
  const invalidReferenceField = tool.prefillFields.find(
    (field) =>
      field.inputRole === 'referenceImage' && !referenceBindingIds.has(field.id)
  );
  if (invalidReferenceField) {
    throw new Error(
      `Workspace prefill Tool ${tool.toolId} referenceImage binding does not contain field ${invalidReferenceField.id}.`
    );
  }

  const parameterFieldIds = new Set(
    tool.prefillFields
      .filter(
        (field) =>
          field.inputRole === 'parameter' && field.control !== 'image_file'
      )
      .map((field) => field.id)
  );
  const parameterFields = tool.fields.filter((field) =>
    parameterFieldIds.has(field.id)
  );
  if (parameterFields.length !== parameterFieldIds.size) {
    throw new Error(
      `Workspace prefill Tool ${tool.toolId} parameter projection does not match its Workspace fields.`
    );
  }

  return {
    version: 1,
    toolId: tool.toolId,
    toolLabel: tool.label,
    uploadLimits: {
      freeMaxSingleBytes: tool.runtimeCaps.freeMaxSingleBytes,
      paidMaxSingleBytes: tool.runtimeCaps.paidMaxSingleBytes,
    },
    creditQuoteTool: {
      toolId: tool.toolId,
      viewKind: tool.viewKind,
      inputBindings: tool.inputBindings,
      fields: tool.fields,
      prefillFields: tool.prefillFields,
      creditPolicy: tool.creditPolicy,
      runtimeCaps: {
        creditCalculationMode: tool.runtimeCaps.creditCalculationMode,
      },
    },
    fields: tool.prefillFields,
    parameterFields,
  };
}

function addIssue(
  issues: WorkspacePrefillValidationIssue[],
  fieldId: string,
  message: string,
  code: WorkspacePrefillValidationIssue['code'] = 'invalid-request'
) {
  issues.push({ code, fieldId, message });
}

function isEmptyRequiredValue(value: unknown) {
  return (
    value === undefined ||
    (typeof value === 'string' && value.trim().length === 0) ||
    (Array.isArray(value) && value.length === 0)
  );
}

function validateTextValue(params: {
  field: WorkspaceToolPrefillFieldView;
  value: unknown;
  issues: WorkspacePrefillValidationIssue[];
}) {
  const { field, value, issues } = params;
  if (typeof value !== 'string') {
    addIssue(issues, field.id, `${field.label} must be text.`);
    return;
  }
  if (field.minLength !== undefined && value.length < field.minLength) {
    addIssue(
      issues,
      field.id,
      `${field.label} must contain at least ${field.minLength} characters.`
    );
  }
  if (field.maxLength !== undefined && value.length > field.maxLength) {
    addIssue(
      issues,
      field.id,
      `${field.label} cannot exceed ${field.maxLength} characters.`
    );
  }
}

function validateNumberValue(params: {
  field: WorkspaceToolPrefillFieldView;
  value: unknown;
  issues: WorkspacePrefillValidationIssue[];
}) {
  const { field, value, issues } = params;
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    addIssue(issues, field.id, `${field.label} must be a finite number.`);
    return;
  }
  if (field.min !== undefined && value < field.min) {
    addIssue(
      issues,
      field.id,
      `${field.label} cannot be less than ${field.min}.`
    );
  }
  if (field.max !== undefined && value > field.max) {
    addIssue(
      issues,
      field.id,
      `${field.label} cannot be greater than ${field.max}.`
    );
  }
}

function hasOption(field: WorkspaceToolPrefillFieldView, value: unknown) {
  return (field.options ?? []).some((option) => Object.is(option.value, value));
}

function validateArrayLength(params: {
  field: WorkspaceToolPrefillFieldView;
  value: unknown[];
  issues: WorkspacePrefillValidationIssue[];
}) {
  const { field, value, issues } = params;
  if (field.minItems !== undefined && value.length < field.minItems) {
    addIssue(
      issues,
      field.id,
      `${field.label} requires at least ${field.minItems} items.`
    );
  }
  if (field.maxItems !== undefined && value.length > field.maxItems) {
    addIssue(
      issues,
      field.id,
      `${field.label} allows at most ${field.maxItems} items.`
    );
  }
}

function validateFieldValue(params: {
  field: WorkspaceToolPrefillFieldView;
  value: unknown;
  issues: WorkspacePrefillValidationIssue[];
}) {
  const { field, value, issues } = params;
  if (field.control === 'text' || field.control === 'url') {
    validateTextValue(params);
    return;
  }
  if (field.control === 'number' || field.control === 'slider') {
    validateNumberValue(params);
    return;
  }
  if (field.control === 'toggle') {
    if (typeof value !== 'boolean') {
      addIssue(issues, field.id, `${field.label} must be true or false.`);
    }
    return;
  }
  if (field.control === 'select') {
    if (!hasOption(field, value)) {
      addIssue(issues, field.id, `${field.label} contains an unknown option.`);
    }
    return;
  }
  if (field.control === 'multi-select') {
    if (!Array.isArray(value)) {
      addIssue(issues, field.id, `${field.label} must be an option list.`);
      return;
    }
    validateArrayLength({ field, value, issues });
    if (value.some((item) => !hasOption(field, item))) {
      addIssue(issues, field.id, `${field.label} contains an unknown option.`);
    }
    return;
  }
  if (field.control === 'array') {
    if (!Array.isArray(value)) {
      addIssue(issues, field.id, `${field.label} must be a list.`);
      return;
    }
    validateArrayLength({ field, value, issues });
    return;
  }

  addIssue(
    issues,
    field.id,
    `${field.label} cannot receive a scalar prefill value.`
  );
}

function validateFieldFiles(params: {
  field: WorkspaceToolPrefillFieldView;
  files: File[];
  issues: WorkspacePrefillValidationIssue[];
}) {
  const { field, files, issues } = params;
  const constraints = field.imageConstraints;
  if (
    constraints?.maxCount !== undefined &&
    files.length > constraints.maxCount
  ) {
    addIssue(
      issues,
      field.id,
      `${field.label} allows at most ${constraints.maxCount} files.`,
      'too-many-files'
    );
  }
  if (
    constraints?.allowedMimeTypes.length &&
    files.some(
      (file) =>
        !canEnhancerUploadFileReachSupportedMimeType(
          file,
          constraints.allowedMimeTypes
        )
    )
  ) {
    addIssue(
      issues,
      field.id,
      `${field.label} contains an unsupported file type.`,
      'unsupported-file-type'
    );
  }
  if (
    constraints?.maxSingleBytes !== undefined &&
    files.some((file) => file.size > constraints.maxSingleBytes!)
  ) {
    addIssue(
      issues,
      field.id,
      `${field.label} contains a file that is too large.`,
      'file-too-large'
    );
  }
  if (
    constraints?.maxTotalBytes !== undefined &&
    files.reduce((total, file) => total + file.size, 0) >
      constraints.maxTotalBytes
  ) {
    addIssue(
      issues,
      field.id,
      `${field.label} exceeds the total file size limit.`,
      'file-too-large'
    );
  }
}

export function validateWorkspacePrefillRequest(
  model: WorkspacePrefillFormModel,
  request: WorkspacePrefillRequestV1
): WorkspacePrefillValidationResult {
  const issues: WorkspacePrefillValidationIssue[] = [];
  const fieldById = new Map(model.fields.map((field) => [field.id, field]));

  for (const fieldId of Object.keys(request.valuesByFieldId)) {
    if (!fieldById.has(fieldId)) {
      addIssue(
        issues,
        fieldId,
        `Tool ${model.toolId} does not expose prefillable field ${fieldId}.`
      );
    }
  }
  for (const fieldId of Object.keys(request.filesByFieldId)) {
    if (!fieldById.has(fieldId)) {
      addIssue(
        issues,
        fieldId,
        `Tool ${model.toolId} does not expose prefillable file field ${fieldId}.`
      );
    }
  }

  for (const field of model.fields) {
    const value = request.valuesByFieldId[field.id];
    const files = request.filesByFieldId[field.id];
    if (field.control === 'image_file') {
      if (value !== undefined) {
        addIssue(
          issues,
          field.id,
          `${field.label} must be provided through filesByFieldId.`
        );
      }
      if (field.required && (!files || files.length === 0)) {
        addIssue(issues, field.id, `${field.label} is required.`);
      }
      if (files) {
        validateFieldFiles({ field, files, issues });
      }
      continue;
    }

    if (files !== undefined) {
      addIssue(
        issues,
        field.id,
        `${field.label} must be provided through valuesByFieldId.`
      );
    }
    if (value === undefined) {
      if (field.required && isEmptyRequiredValue(field.defaultValue)) {
        addIssue(issues, field.id, `${field.label} is required.`);
      }
      continue;
    }
    if (field.required && isEmptyRequiredValue(value)) {
      addIssue(issues, field.id, `${field.label} is required.`);
      continue;
    }
    validateFieldValue({ field, value, issues });
  }

  if (issues.length > 0) {
    return { ok: false, issues };
  }

  return { ok: true, value: request };
}
